import { inject, Injectable } from '@angular/core';
import {
   collection,
   CollectionReference,
   deleteDoc,
   doc,
   Firestore,
   getDoc,
   getDocs,
   serverTimestamp,
   setDoc,
   Timestamp,
   updateDoc,
} from '@angular/fire/firestore';
import { collectionData } from '@angular/fire/firestore';
import { FeatureFlag } from '../models/feature-flag.model';
import { isFeatureEnabledForPackage, Package } from '../models/package.model';
import { UserService } from '../user/user.service';
import { PackageService } from './package.service';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
   private firestore = inject(Firestore);
   private userService = inject(UserService);
   private packageService = inject(PackageService);

   private flagsRef = collection(
      this.firestore,
      'feature_flags',
   ) as CollectionReference<FeatureFlag>;

   /** Real-time observable of all feature flags */
   flags$: Observable<FeatureFlag[]> = collectionData(this.flagsRef, {
      idField: 'key',
   });

   private currentFlags: FeatureFlag[] = [];

   /** True once the realtime `flags$` feed has emitted at least once. */
   private flagsLoaded$ = new BehaviorSubject<boolean>(false);

   constructor() {
      this.flags$.subscribe((flags) => {
         this.currentFlags = flags;
         this.flagsLoaded$.next(true);
      });
   }

   /** Get all feature flags */
   async getAll(): Promise<FeatureFlag[]> {
      const snap = await getDocs(this.flagsRef);
      return snap.docs.map((d) => d.data());
   }

   /** Get a single flag by key (always a one-shot network/cache read) */
   async getFlag(key: string): Promise<FeatureFlag | null> {
      const snap = await getDoc(doc(this.flagsRef, key));
      return snap.exists() ? snap.data() : null;
   }

   /**
    * Resolve a flag for the async checks/route guard without paying a Firestore
    * round-trip on every navigation.
    *
    * Steady state: the realtime `flags$` feed has already populated
    * `currentFlags`, so this returns synchronously from memory. Cold state
    * (e.g. a hard reload straight into a gated route): wait briefly for the
    * first feed emission, and only if it doesn't arrive fall back to a one-shot
    * `getFlag` read so gating still resolves.
    */
   private async getFlagCached(key: string): Promise<FeatureFlag | null> {
      if (!this.flagsLoaded$.value) {
         const warmed = await Promise.race([
            firstValueFrom(
               this.flagsLoaded$.pipe(
                  filter((v) => v),
                  take(1),
               ),
            ),
            new Promise<boolean>((resolve) =>
               setTimeout(() => resolve(false), 3000),
            ),
         ]);
         if (!warmed) return this.getFlag(key);
      }
      return this.currentFlags.find((f) => f.key === key) ?? null;
   }

   /**
    * Resolve the package that gates features for a store.
    *
    * Feature resolution is package-aware only for the *current* store (whose
    * full doc — including `packageId` — is held in memory). For any other
    * store id, or when no package is assigned, we return null and fall back to
    * the legacy global-toggle behaviour.
    */
   private resolvePackage(storeId: string): Package | null {
      const current = this.userService.currentStore$.value;
      if (!current || current.id !== storeId) return null;
      return this.packageService.getByIdSync(current.packageId);
   }

   /**
    * Authoritative package resolution for a store — used by the async
    * checks/route guard so gating is correct even during a cold navigation
    * before the in-memory store/package caches are warm.
    */
   private resolvePackageAsync(storeId: string): Promise<Package | null> {
      return this.packageService.getForStore(storeId);
   }

   /**
    * Check if a feature is enabled for a specific store.
    * Returns true when the flag doesn't exist yet (safe default = visible).
    */
   async isEnabled(featureKey: string, storeId: string): Promise<boolean> {
      const flag = await this.getFlagCached(featureKey);
      if (!flag) return true; // not yet managed → visible by default
      return isFeatureEnabledForPackage(
         flag,
         storeId,
         await this.resolvePackageAsync(storeId),
      );
   }

   /**
    * Strict async check: unlike {@link isEnabled}, an absent (unmanaged) flag
    * counts as DISABLED. Use for features that must be hidden until explicitly
    * turned on in Feature Management.
    */
   async isExplicitlyEnabled(
      featureKey: string,
      storeId: string,
   ): Promise<boolean> {
      const flag = await this.getFlagCached(featureKey);
      if (!flag) return false; // not yet managed → hidden
      return isFeatureEnabledForPackage(
         flag,
         storeId,
         await this.resolvePackageAsync(storeId),
      );
   }

   /**
    * Synchronous check if a feature is enabled based on cached realtime data.
    * Use this for guards and UI components.
    */
   isEnabledSync(featureKey: string, storeId: string): boolean {
      const flag = this.currentFlags.find((f) => f.key === featureKey);
      if (!flag) return true; // not yet managed → visible by default
      return isFeatureEnabledForPackage(flag, storeId, this.resolvePackage(storeId));
   }

   /**
    * Strict synchronous check: an absent (unmanaged) flag counts as DISABLED.
    * Use for UI that must stay hidden until the feature is explicitly enabled.
    */
   isExplicitlyEnabledSync(featureKey: string, storeId: string): boolean {
      const flag = this.currentFlags.find((f) => f.key === featureKey);
      if (!flag) return false; // not yet managed → hidden
      return isFeatureEnabledForPackage(flag, storeId, this.resolvePackage(storeId));
   }

   /** Create or fully overwrite a flag document */
   async upsert(flag: FeatureFlag): Promise<void> {
      const currentUser = this.userService.currentUser$.value;
      const data: FeatureFlag = {
         ...flag,
         updatedAt: serverTimestamp() as unknown as Timestamp,
         updatedBy: {
            uid: currentUser?.id || '',
            displayName: currentUser?.displayName || '',
         },
      };
      if (!data.createdAt) {
         data.createdAt = serverTimestamp() as unknown as Timestamp;
      }
      await setDoc(doc(this.flagsRef, flag.key), data);
   }

   /** Permanently delete a feature flag document. */
   async deleteFlag(key: string): Promise<void> {
      await deleteDoc(doc(this.flagsRef, key));
   }

   /** Toggle global enabled state */
   async setGlobalEnabled(key: string, enabled: boolean): Promise<void> {
      const currentUser = this.userService.currentUser$.value;
      await updateDoc(doc(this.flagsRef, key), {
         globalEnabled: enabled,
         updatedAt: serverTimestamp() as unknown as Timestamp,
         updatedBy: {
            uid: currentUser?.id || '',
            displayName: currentUser?.displayName || '',
         },
      });
   }

   /** Add a store to the allowedStoreIds list (whitelist) */
   async addAllowedStore(key: string, storeId: string): Promise<void> {
      const flag = await this.getFlag(key);
      if (!flag) return;
      const ids = Array.from(new Set([...flag.allowedStoreIds, storeId]));
      await this._updateOverrides(key, { allowedStoreIds: ids });
   }

   /** Remove a store from the allowedStoreIds list */
   async removeAllowedStore(key: string, storeId: string): Promise<void> {
      const flag = await this.getFlag(key);
      if (!flag) return;
      const ids = flag.allowedStoreIds.filter((id) => id !== storeId);
      await this._updateOverrides(key, { allowedStoreIds: ids });
   }

   /** Add a store to the blockedStoreIds list (blacklist) */
   async addBlockedStore(key: string, storeId: string): Promise<void> {
      const flag = await this.getFlag(key);
      if (!flag) return;
      const ids = Array.from(new Set([...flag.blockedStoreIds, storeId]));
      await this._updateOverrides(key, { blockedStoreIds: ids });
   }

   /** Remove a store from the blockedStoreIds list */
   async removeBlockedStore(key: string, storeId: string): Promise<void> {
      const flag = await this.getFlag(key);
      if (!flag) return;
      const ids = flag.blockedStoreIds.filter((id) => id !== storeId);
      await this._updateOverrides(key, { blockedStoreIds: ids });
   }

   private async _updateOverrides(
      key: string,
      data: Partial<FeatureFlag>,
   ): Promise<void> {
      const currentUser = this.userService.currentUser$.value;
      await updateDoc(doc(this.flagsRef, key), {
         ...data,
         updatedAt: serverTimestamp() as unknown as Timestamp,
         updatedBy: {
            uid: currentUser?.id || '',
            displayName: currentUser?.displayName || '',
         },
      });
   }
}
